import { Injectable, inject } from '@angular/core';
import { AuthService } from '../auth/auth.service';

export type OrgKind = 'charity' | 'business';

export const LAST_USED_CHARITY_KEY_PREFIX: string = 'lastUsedCharityId';
export const LAST_USED_BUSINESS_KEY_PREFIX: string = 'lastUsedBusinessId';

/**
 * Owns per-account localStorage persistence of the last-used org id for each
 * portal. Keys are scoped by authenticated user id so two accounts on the same
 * device don't inherit each other's selection.
 *
 * All reads and writes are wrapped in try/catch — storage may be unavailable
 * (restricted contexts, private browsing quota, test stubs). A failed write
 * is a silent no-op; a failed read returns null. Callers must never rely on
 * persistence succeeding.
 */
@Injectable({ providedIn: 'root' })
export class OrgContextStorageService {
  private readonly authService = inject(AuthService);

  setLastUsedOrgId(kind: OrgKind, orgId: string): void {
    const key: string | null = this.buildKey(kind);
    if (!key) return;
    try {
      localStorage.setItem(key, orgId);
    } catch {
      // Storage unavailable or quota exceeded — persistence is best-effort
    }
  }

  getLastUsedOrgId(kind: OrgKind): number | null {
    const key: string | null = this.buildKey(kind);
    if (!key) return null;
    try {
      const raw: string | null = localStorage.getItem(key);
      if (!raw) return null;
      const id: number = Number(raw);
      return Number.isInteger(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }

  private buildKey(kind: OrgKind): string | null {
    const userId: string | undefined = this.authService.currentUser()?.id;
    if (!userId) return null;
    const prefix: string =
      kind === 'charity' ? LAST_USED_CHARITY_KEY_PREFIX : LAST_USED_BUSINESS_KEY_PREFIX;
    return `${prefix}:${userId}`;
  }
}
